<template>
  <Modal
    v-model="show"
    type="card"
    :full-height="true"
    :show-header="false"
    :padding="false"
    :closeable="true"
    :persistent="true"
  >
    <div class="profiency-level-changed-modal">
      <button
        v-if="showMainAnimation"
        class="modal__close-button"
        variant="text"
        rounded
        @click="show = false"
      >
        <img src="/images/close-white.svg" width="12" height="12" />
      </button>

      <div
        v-if="showMainAnimation"
        class="profiency-level-changed-modal__content"
      >
        <div
          class="profiency-level-changed-modal__title animate__animated animate__fadeInDown"
        >
          {{ firstName ?? 'Hey' }}
        </div>
        <div
          class="profiency-level-changed-modal__description animate__animated animate__fadeInDown animate__delay-1s"
        >
          Você subiu de nível de conhecimento em inglês!
        </div>
        <div
          class="profiency-level-changed-modal__level animate__animated animate__bounceIn"
        >
          <div class="profiency-level-changed-modal__level-title">
            {{ proficiencyLevel }}
          </div>
          <div class="profiency-level-changed-modal__level-description">
            {{ proficiencyLevelDescription }}
          </div>
        </div>

        <button
          class="profiency-level-changed-modal__button animate__animated animate__fadeIn animate__delay-3s"
          @click="show = false"
        >
          Acessar outras atividades
        </button>
        <button
          class="profiency-level-changed-modal__button_outline animate__animated animate__fadeIn animate__delay-3s"
          @click="openExplanationModal = true"
        >
          Entender meu nível
        </button>
      </div>

      <lottie-player
        v-if="showBackgroundAnimation"
        src="/lottie/modal-level-changed.json"
        background="transparent"
        speed="1"
        :loop="false"
        autoplay
        class="background-animation"
      />

      <lottie-player
        v-if="showConfettiAnimation"
        src="/lottie/confetti.json"
        background="transparent"
        speed="1"
        :loop="true"
        autoplay
        class="confetti-animation"
      />

      <ClapsAnimation v-if="showClapsAnimation" />
    </div>
  </Modal>

  <ProficiencyLevelModal v-model="openExplanationModal" />
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';

import Modal from '@/components/Modal/index.vue';
import ProficiencyLevelModal from '@/components/ProficiencyLevel/Modal.vue';
import ClapsAnimation from '@/components/Session/Interaction/Output/Templates/ClapsAnimation.vue';
import sleep from '@/helpers/common/sleep';
import { useAuthStore } from '@/store/auth';

const openExplanationModal = ref(false);

const props = defineProps<{
  modelValue: boolean;
}>();

const show = ref(props.modelValue);
const $emit = defineEmits(['update:modelValue']);

watch(
  () => show.value,
  () => {
    $emit('update:modelValue', show.value);
  },
);

watch(
  () => props.modelValue,
  () => {
    show.value = props.modelValue;
  },
);

const showBackgroundAnimation = ref(false);
const showConfettiAnimation = ref(false);
const showClapsAnimation = ref(false);
const showMainAnimation = ref(show.value);

const authStore = useAuthStore();
const firstName = computed(() => authStore.user?.firstName);
const proficiencyLevel = ref(authStore.lastProficiencyLevel);
const proficiencyLevelDescription = computed(() => {
  switch (proficiencyLevel.value) {
    case 'A0':
      return 'Nível Iniciante';
    case 'A1':
      return 'Nível Iniciante';
    case 'A2':
      return 'Nível Básico';
    case 'B1':
      return 'Nível Intermediário';
    case 'B2':
      return 'Nível Avançado';
    case 'C1':
      return 'Nível Avançado';
    case 'C2':
      return 'Nível Avançado';
    default:
      return 'Nível Iniciante';
  }
});

function playAudio() {
  const audio = new Audio('/audio/activity-finish.mp3');
  audio.play();
  audio.remove();
}

async function playAnimation() {
  if (showMainAnimation.value) {
    if (authStore.user?.proficiencyLevel) {
      proficiencyLevel.value = authStore.user?.proficiencyLevel;
    }

    return;
  }

  await sleep(100);

  showBackgroundAnimation.value = true;

  setTimeout(() => {
    showBackgroundAnimation.value = false;
    showMainAnimation.value = true;

    setTimeout(() => {
      showConfettiAnimation.value = true;
      playAudio();

      setTimeout(() => {
        showConfettiAnimation.value = false;

        setTimeout(() => {
          if (authStore.user?.proficiencyLevel) {
            proficiencyLevel.value = authStore.user?.proficiencyLevel;
          }

          showClapsAnimation.value = true;

          setTimeout(() => {
            showClapsAnimation.value = false;
          }, 1500);
        }, 500);
      }, 1000);
    }, 900);
  }, 1400);
}

onMounted(() => {
  if (show.value) {
    playAnimation();
  }
});

watch(show, () => {
  if (show.value) {
    playAnimation();
  } else {
    authStore.setLastProficiencyLevel(proficiencyLevel.value);
  }
});
</script>

<style scoped>
.profiency-level-changed-modal {
  background-color: var(--color-brand-04);
  height: calc(100vh - 48px);
}
.modal__close-button {
  position: absolute;
  right: 20px;
  top: 20px;
  border: 1px solid var(--color-neutral-02);
  border-radius: 100%;
  width: 32px;
  height: 32px;
}
.profiency-level-changed-modal__content {
  color: var(--color-contrast-oninteractive);
}

.background-animation,
.confetti-animation {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0px;
  left: 0px;
}

.profiency-level-changed-modal__title {
  font-size: 24px;
  text-align: center;
}

.profiency-level-changed-modal__content {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  height: 100%;
  text-align: center;
  padding-left: 20px;
  padding-right: 20px;
  padding-top: 124px;
  padding-bottom: 32px;
}

.profiency-level-changed-modal__description {
  font-size: 24px;
  line-height: 120%;
  font-weight: 600;
  margin-top: 12px;
  margin-bottom: 46px;
}

.profiency-level-changed-modal__level {
  flex: 1 1 auto;
  animation-delay: 1.2s;
}

.profiency-level-changed-modal__level-title {
  font-size: 116px;
  font-weight: bold;
  line-height: 120%;
}

.profiency-level-changed-modal__level-description {
  font-size: 20px;
  line-height: 120%;
}

.profiency-level-changed-modal__button {
  background: var(--color-contrast-oninteractive);
  width: 100%;
  height: 48px;
  border-radius: 510px;
  color: var(--color-neutral-06);
  font-size: 14px;
  margin-bottom: 24px;
}

.profiency-level-changed-modal__button_outline {
  background: transparent;
  width: 100%;
  height: 48px;
  border-radius: 510px;
  color: var(--color-contrast-oninteractive);
  font-size: 14px;
  border: 1px solid var(--color-contrast-oninteractive);
}
</style>
